export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSection {
  title: string;
  clientItems?: FAQItem[];
  stylistItems?: FAQItem[];
}

export const FAQ_DATA: FAQSection[] = [
  {
    title: 'Getting Started',
    clientItems: [
      {
        question: "Why can't I log in on the website?",
        answer:
          "EMME is currently iOS-only for clients. If you try to log in via web, you'll see a message prompting you to use the app.",
      },
    ],
    stylistItems: [
      {
        question: 'How do I become a stylist on EMME?',
        answer:
          "Stylists must apply via our website. If accepted, you'll receive an invite email with login details.",
      },
      {
        question: "I didn't get my email verification — what should I do?",
        answer:
          'Check your spam folder, or request a resend by reaching out to support@emmestyling.com.',
      },
    ],
  },
  {
    title: 'Account & Profile',
    clientItems: [
      {
        question: 'Can I edit my profile?',
        answer:
          'Yes, you can edit your style preferences and personal details in your profile section of the app.',
      },
    ],
    stylistItems: [
      {
        question: "What's required in my profile?",
        answer:
          "You'll need a profile picture, bio, hourly rate, style and expertise tags, and at least one portfolio image.",
      },
      {
        question: 'Can I update my price tier or gallery?',
        answer:
          'You can update your gallery anytime. Price tiers are managed by the EMME team — request a review via support@emmestyling.com.',
      },
    ],
  },
  {
    title: 'Booking & Sessions',
    clientItems: [
      {
        question: 'What are my options for styling sessions?',
        answer:
          'Choose between Instant Styling (on-demand match) or Scheduled Sessions (book in advance).',
      },
      {
        question: 'How far in advance can I book?',
        answer:
          'You can schedule a session up to 30 days ahead. Bookings must be made at least 24 hours in advance.',
      },
      {
        question: "What happens if my stylist doesn't show up?",
        answer:
          "You won't be charged. We'll follow up and help you reschedule.",
      },
    ],
    stylistItems: [
      {
        question: 'How do I manage my calendar?',
        answer:
          'You can sync your availability via Google Calendar using Nylas. This is done from the desktop dashboard.',
      },
      {
        question: "What's the minimum notice for scheduled bookings?",
        answer:
          "Clients can't book within 24 hours. You'll always have at least a day's notice.",
      },
    ],
  },
  {
    title: 'During the Call',
    clientItems: [
      {
        question: 'Where do the sessions take place?',
        answer: 'All sessions happen via video calls in the app.',
      },
      {
        question: 'Can I rejoin if I accidentally leave?',
        answer:
          'Yes — the session will remain in progress. You can rejoin from the Sessions screen.',
      },
    ],
    stylistItems: [
      {
        question: 'Can I join from my phone?',
        answer:
          'Stylists must join via desktop or mobile app. Mobile web is not supported for live video.',
      },
      {
        question: 'What if a client disconnects?',
        answer:
          "Wait a few minutes — they may rejoin. You can end the session after 15 minutes if they don't return.",
      },
    ],
  },
  {
    title: 'Payments & Payouts',
    clientItems: [
      {
        question: 'When am I charged?',
        answer:
          'Stripe places a temporary hold on your card when you book (time and a half of your booked time for in-person sessions, or your booked time plus 30 minutes for virtual sessions) and charges you after it ends based on the actual duration. The rest of the hold is released.',
      },
      {
        question: 'How much will I pay?',
        answer:
          "You'll be billed for a minimum of 15 minutes. Anything over that is charged per 15 minute intervals, up to a max of 60 minutes.",
      },
      {
        question: 'What if I need a refund?',
        answer:
          'Refunds are handled automatically for cancellations made 24+ hours in advance. For other issues, contact support@emmestyling.com.',
      },
    ],
    stylistItems: [
      {
        question: 'How do I get paid?',
        answer:
          'Payments are processed via Stripe Connect. Payouts happen bi-weekly.',
      },
      {
        question: 'Does EMME take a cut?',
        answer:
          'EMME collects a 20% service fee on paid sessions and upsells like Lookbooks.',
      },
    ],
  },
  {
    title: 'Instant Styling',
    clientItems: [
      {
        question: 'How do I start an instant session?',
        answer:
          "Choose your preferences (duration, price tier, tags), and we'll find a match for you in real time.",
      },
      {
        question: "What happens if I don't like the match?",
        answer:
          "You can search again up to 3 times. If no one is a fit, you'll be prompted to schedule instead.",
      },
    ],
    stylistItems: [
      {
        question: 'How do I get included in instant styling?',
        answer:
          'Toggle "Instant Styling" on from the dashboard. You\'ll be available for up to 24 hours, until a session starts, or until you toggle it off.',
      },
      {
        question: 'What if I miss a request?',
        answer:
          "If you don't respond within 60 seconds, the request times out and you won't be shown again to that client this round.",
      },
    ],
  },
  {
    title: 'Messaging & Lookbooks',
    clientItems: [
      {
        question: 'When can I message my stylist?',
        answer:
          'After a scheduled session is confirmed or an instant session ends, chat opens in your session details view.',
      },
      {
        question: 'Can I purchase a Lookbook after a session?',
        answer:
          "Yes — your stylist can send you an offer in chat. You'll see a price and can purchase via Stripe.",
      },
    ],
    stylistItems: [
      {
        question: 'How do I send a Lookbook offer?',
        answer:
          'In the chat, send a Lookbook with a custom price ($50+). Upload your PDF once purchased.',
      },
      {
        question: 'Who can end a chat?',
        answer:
          'Stylists are considered the chat owner and can end the conversation if needed.',
      },
    ],
  },
  {
    title: 'Notifications',
    clientItems: [
      {
        question: 'How will I know if I have a new message or booking?',
        answer:
          "You'll receive in-app notifications, push notifications (if enabled), and email confirmations.",
      },
      {
        question: 'Where can I see all my notifications?',
        answer:
          'Tap the bell icon in the app to access your Notification Center.',
      },
    ],
    stylistItems: [
      {
        question: 'How will I know if I have a new message or booking?',
        answer:
          "You'll receive in-app notifications, push notifications (if enabled), and email confirmations.",
      },
      {
        question: 'Where can I see all my notifications?',
        answer:
          'Tap the bell icon in the app to access your Notification Center.',
      },
    ],
  },
  {
    title: 'Cancellations & No-Shows',
    clientItems: [
      {
        question: 'Can I cancel a session?',
        answer:
          "Yes — cancel up to 24 hours in advance for a full refund. After that, you'll be charged the full session fee.",
      },
      {
        question: 'What if I forget to join a call?',
        answer:
          "This is considered a no-show. You'll be charged for the full estimated session time.",
      },
    ],
    stylistItems: [
      {
        question: 'What if I need to cancel a session?',
        answer:
          'You can cancel up to 24 hours in advance. Last-minute cancellations may affect your standing.',
      },
    ],
  },
  {
    title: 'Support',
    clientItems: [
      {
        question: 'How do I contact support?',
        answer: 'Email support@emmestyling.com.',
      },
      {
        question: 'Where can I read the Privacy Policy or Terms?',
        answer:
          "These are available in the app's sub navigation and in the footer of the landing page.",
      },
      {
        question: 'How do I delete my EMME account?',
        answer:
          'If you\'d like to delete your account, email us at support@emmestyling.com with the subject line "Account Deletion Request." We\'ll permanently delete your account and data within 7 days.',
      },
    ],
    stylistItems: [
      {
        question: 'How do I contact support?',
        answer: 'Email support@emmestyling.com.',
      },
      {
        question: 'Where can I read the Privacy Policy or Terms?',
        answer:
          "These are available in the app's sub navigation and in the footer of the landing page.",
      },
      {
        question: 'How do I delete my EMME account?',
        answer:
          'If you\'d like to delete your account, email us at support@emmestyling.com with the subject line "Account Deletion Request." We\'ll permanently delete your account and data within 7 days.',
      },
    ],
  },
];
